/**
 * SessionStatusBadge Component
 *
 * Displays a colored badge indicating the telehealth session status
 */

import React from 'react';
import {
  Clock,
  Users,
  Video,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Ban,
} from 'lucide-react';
import {
  SessionStatus,
  SessionStatusBadgeProps,
  SESSION_STATUS_LABELS,
} from './types';

const statusConfig: Record<
  SessionStatus,
  { bg: string; text: string; icon: React.ComponentType<{ className?: string }> }
> = {
  scheduled: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    icon: Clock,
  },
  waiting: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-300',
    icon: Users,
  },
  in_progress: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
    icon: Video,
  },
  completed: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
    icon: CheckCircle,
  },
  no_show: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-300',
    icon: XCircle,
  },
  cancelled: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-500 dark:text-gray-400',
    icon: Ban,
  },
  technical_failure: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-300',
    icon: AlertTriangle,
  },
};

const sizeConfig = {
  sm: {
    badge: 'px-2 py-0.5 text-xs',
    icon: 'h-3 w-3',
  },
  md: {
    badge: 'px-2.5 py-1 text-sm',
    icon: 'h-4 w-4',
  },
  lg: {
    badge: 'px-3 py-1.5 text-base',
    icon: 'h-5 w-5',
  },
};

export default function SessionStatusBadge({
  status,
  size = 'md',
  showIcon = true,
  className = '',
}: SessionStatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.scheduled;
  const sizeStyles = sizeConfig[size];
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${config.bg} ${config.text} ${sizeStyles.badge} ${className}`}
    >
      {showIcon && <Icon className={sizeStyles.icon} />}
      <span>{SESSION_STATUS_LABELS[status] || status}</span>
    </span>
  );
}

// Also export a simpler dot indicator
export function SessionStatusDot({
  status,
  className = '',
}: {
  status: SessionStatus;
  className?: string;
}) {
  const colorMap: Record<SessionStatus, string> = {
    scheduled: 'bg-blue-500',
    waiting: 'bg-yellow-500',
    in_progress: 'bg-green-500',
    completed: 'bg-gray-400',
    no_show: 'bg-red-500',
    cancelled: 'bg-gray-400',
    technical_failure: 'bg-orange-500',
  };

  const pulseStatuses: SessionStatus[] = ['waiting', 'in_progress'];
  const shouldPulse = pulseStatuses.includes(status);

  return (
    <span className={`relative inline-flex ${className}`}>
      <span
        className={`h-2.5 w-2.5 rounded-full ${colorMap[status] || 'bg-gray-400'}`}
      />
      {shouldPulse && (
        <span
          className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${colorMap[status] || 'bg-gray-400'}`}
        />
      )}
    </span>
  );
}
